import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IncludeInventoryLengthsToggle } from '~/components/common/IncludeInventoryLengthsToggle';
import { Input } from '~/components/ui';
import { SavedViewsFilterControls } from '~/components/ui/SavedViewsFilterControls';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import type { UniqueLocationsWithInventoryQuery } from '~/graphql/graphql';
import { useDebounce } from '~/hooks/useDebounce';
import { formatLocationName } from '~/utils';
import {
  type BranchSelection,
  branchSelectOptions,
  parseBranchSelection,
  serializeBranchSelection,
} from '~/pages/buyer-inventory/lib/branchSelection';
import { activeRowCount } from '~/pages/buyer-inventory/lib/buyerInventoryFilterStorage';
import { getInventoryFilterFields } from '~/pages/buyer-inventory/lib/inventoryFilterFields';
import type { InventorySavedViews } from '~/pages/buyer-inventory/lib/useInventorySavedViews';
import { InventoryFilterPanel } from './InventoryFilterPanel';

const SEARCH_DEBOUNCE_MS = 500;

type BuyerInventoryToolbarProps = {
  readonly branchSelection: BranchSelection;
  readonly locations: UniqueLocationsWithInventoryQuery['uniqueLocationsWithInventory'];
  readonly onBranchChange: (selection: BranchSelection) => void;
  readonly onDebouncedSearchChange: (term: string) => void;
  readonly views: InventorySavedViews;
  readonly includeInventoryLengths: boolean;
  readonly onIncludeInventoryLengthsChange: (value: boolean) => void;
};

export function BuyerInventoryToolbar({
  branchSelection,
  locations,
  onBranchChange,
  onDebouncedSearchChange,
  views,
  includeInventoryLengths,
  onIncludeInventoryLengthsChange,
}: Readonly<BuyerInventoryToolbarProps>) {
  const { t } = useTranslation('inventory');
  const fieldOptions = useMemo(() => getInventoryFilterFields(t), [t]);
  const [searchTerm, setSearchTerm] = useState('');
  const emitSearchChange = useDebounce(
    onDebouncedSearchChange,
    SEARCH_DEBOUNCE_MS
  );

  return (
    <div className="px-4 py-3 border-b border-neut-200 flex flex-col sm:flex-row gap-4 items-start justify-between">
      <div className="flex items-center gap-4 flex-wrap">
        <SavedViewsFilterControls
          namespace="inventory"
          keyPrefix="inventory.filter"
          views={views}
          activeCount={activeRowCount(views.rows)}
          buttonTestId="inventory-filter-button"
          countTestId="inventory-filter-count"
          popoverAlign="start"
          renderPanel={({ maxHeight, bottomContent }) => (
            <InventoryFilterPanel
              maxHeight={maxHeight}
              bottomContent={bottomContent}
              rows={views.rows}
              fieldOptions={fieldOptions}
              onRowsChange={views.setRows}
              onClearAll={views.clearActiveView}
            />
          )}
        />
      </div>
      <div
        className="w-full sm:max-w-[35rem] sm:ml-auto flex flex-col gap-3"
        data-testid="inventory-search-column"
      >
        <Input
          name="searchTerm"
          value={searchTerm}
          onChange={(_, value) => {
            setSearchTerm(String(value));
            emitSearchChange(String(value));
          }}
          placeholder={t('inventory.filter.search_placeholder')}
          leftIcon={<Search size={18} className="text-neut-sec" />}
          className="w-full py-2.5 text-sm font-normal"
          containerClassName="w-full sm:w-full"
        />
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="w-full">
            <SearchableSelect
              label={t('inventory.branch')}
              name="branch"
              value={serializeBranchSelection(branchSelection)}
              onChange={(_name, value) =>
                onBranchChange(parseBranchSelection(value))
              }
              options={branchSelectOptions(locations, {
                all: t('inventory.filter.all_branches'),
                noBranchCode: t('inventory.filter.no_branch_code'),
                branch: (code, count) =>
                  t('inventory.filter.branch_option', { code, count }),
                singleLocationBranch: (code, location) =>
                  t('inventory.filter.single_location_branch_option', {
                    code,
                    location: formatLocationName(location),
                  }),
                location: formatLocationName,
              })}
            />
          </div>
          <IncludeInventoryLengthsToggle
            checked={includeInventoryLengths}
            onChange={onIncludeInventoryLengthsChange}
            className="whitespace-nowrap"
          />
        </div>
      </div>
    </div>
  );
}
